import { z } from 'zod';
import type { Result } from './result';
import { toContractError, type ContractError } from './errors';
import { UuidSchema } from './uuid';

const AuthClientSchema = z.object({
    type: z.literal('auth'),
    token: z.string(),
    trace_id: UuidSchema,
    session_id: UuidSchema.optional(),
    robot_id: UuidSchema.optional(),
    lease_token: z.string().optional(),
});

const AuthStatusSchema = z.object({
    type: z.literal('auth'),
    status: z.union([z.literal('ok'), z.literal('error')]),
});

const AuthErrorSchema = z.object({
    error: z.string(),
});

const PingClientSchema = z.object({
    type: z.literal('ws_ping'),
    trace_id: UuidSchema.optional(),
});

const PongSchema = z.object({
    type: z.literal('ws_pong'),
});

const WebRtcHandlerSchema = z.union([z.literal('webrtc'), z.literal('game_user')]);
const GamepadHandlerSchema = z.union([z.literal('gamepad'), z.literal('game_user')]);

const WebRtcOfferSchema = z
    .object({
        handler: WebRtcHandlerSchema,
        type: z.literal('offer'),
        sdp: z.string(),
        id: z.string(),
        connection_id: z.string().optional(),
        target: z.string().optional(),
        robot_id: UuidSchema.optional(),
        session_id: UuidSchema.optional(),
        client_id: z.string().optional(),
    })
    .passthrough();

const WebRtcAnswerSchema = z
    .object({
        handler: WebRtcHandlerSchema,
        type: z.literal('answer'),
        sdp: z.string().optional(),
        id: z.string(),
        connection_id: z.string().optional(),
        robot_id: UuidSchema.optional(),
        session_id: UuidSchema.optional(),
        client_id: z.string().optional(),
    })
    .passthrough();

const WebRtcIceCandidateSchema = z
    .object({
        handler: WebRtcHandlerSchema,
        type: z.literal('ice-candidate'),
        candidate: z.unknown(),
        id: z.string(),
        connection_id: z.string().optional(),
        robot_id: UuidSchema.optional(),
        session_id: UuidSchema.optional(),
        client_id: z.string().optional(),
    })
    .passthrough();

const GamepadSchema = z
    .object({
        handler: GamepadHandlerSchema,
        type: z.literal('gamepad'),
        session_id: UuidSchema,
        robot_id: UuidSchema,
        axes: z.tuple([z.number(), z.number(), z.number()]),
        buttons: z.array(z.string()),
    })
    .passthrough();

const GamepadRegisterSchema = z
    .object({
        handler: GamepadHandlerSchema,
        type: z.literal('register'),
        session_id: UuidSchema,
        robot_id: UuidSchema,
    })
    .passthrough();

const BotSummarySchema = z
    .object({
        id: z.string(),
        name: z.string().optional().nullable(),
        type: z.string().optional().nullable(),
        status: z.string().optional().nullable(),
        image: z.string().optional().nullable(),
        telemetry: z.record(z.unknown()).optional().nullable(),
        teleop_status: z.record(z.unknown()).optional().nullable(),
        teleop_status_at: z.string().optional().nullable(),
        channel: z.string().optional().nullable(),
    })
    .passthrough();

const BotRegisterSchema = z
    .object({
        handler: z.literal('bot'),
        type: z.literal('register'),
        data: BotSummarySchema,
    })
    .passthrough();

const BotListSchema = z.object({
    type: z.literal('list'),
    data: z.array(BotSummarySchema),
});

const BotListRequestSchema = z
    .object({
        handler: z.literal('bot'),
        type: z.literal('list'),
    })
    .passthrough();

const BotRegistrationSchema = z.object({
    type: z.literal('registration'),
    data: BotSummarySchema,
});

const OperatorReadySchema = z
    .object({
        handler: z.literal('operator'),
        type: z.literal('operator_ready'),
        session_id: UuidSchema,
        robot_id: UuidSchema.optional(),
    })
    .passthrough();

const AttemptTimeoutSchema = z
    .object({
        handler: z.literal('operator'),
        type: z.literal('attempt_timeout'),
        session_id: UuidSchema,
        attempt_id: UuidSchema,
        robot_id: UuidSchema.optional(),
    })
    .passthrough();

const AttemptReadySchema = z
    .object({
        type: z.union([z.literal('attempt_ready'), z.literal('attempt_wait')]),
        session_id: UuidSchema,
        reason: z.string().optional(),
        handler: z.string().optional(),
    })
    .passthrough();

const AttemptStartSchema = z
    .object({
        type: z.union([z.literal('attempt_start'), z.literal('attempt_started')]),
        session_id: UuidSchema,
        attempt_id: UuidSchema.optional(),
        attempt_no: z.number().optional(),
        duration_seconds: z.union([z.number(), z.string()]).optional(),
        duration: z.union([z.number(), z.string()]).optional(),
        seconds: z.union([z.number(), z.string()]).optional(),
        robot_id: UuidSchema.nullable().optional(),
        robot_name: z.string().nullable().optional(),
        handler: z.string().optional(),
    })
    .passthrough();

const AttemptResultSchema = z
    .object({
        type: z.literal('attempt_result'),
        session_id: UuidSchema,
        attempt_id: UuidSchema.optional(),
        success: z.union([z.boolean(), z.string()]).optional(),
        reason: z.string().optional(),
        robot_id: UuidSchema.optional(),
        robot_name: z.string().nullable().optional(),
        timestamp: z.string().optional(),
        handler: z.string().optional(),
    })
    .passthrough();

const SessionEventSchema = z
    .object({
        type: z.literal('session_event'),
        session_id: UuidSchema,
        event_type: z.string().optional(),
        payload: z
            .object({
                attempt_id: UuidSchema.optional(),
                ts: z.string().optional(),
            })
            .passthrough()
            .optional(),
        handler: z.string().optional(),
    })
    .passthrough();

const TeleopStatusSchema = z
    .object({
        type: z.literal('teleop_status'),
        handler: z.string().optional(),
        teleop_status: z.record(z.unknown()).optional(),
    })
    .passthrough();

const ModeChangeSchema = z
    .object({
        type: z.literal('mode-change'),
        mode: z.string(),
        botId: z.string().optional(),
    })
    .passthrough();

export const ControlMessageSchema = z.union([
    AuthStatusSchema,
    AuthErrorSchema,
    PongSchema,
    WebRtcOfferSchema,
    WebRtcAnswerSchema,
    WebRtcIceCandidateSchema,
    GamepadSchema,
    GamepadRegisterSchema,
    BotRegisterSchema,
    BotListSchema,
    BotListRequestSchema,
    BotRegistrationSchema,
    OperatorReadySchema,
    AttemptTimeoutSchema,
    AttemptReadySchema,
    AttemptStartSchema,
    AttemptResultSchema,
    SessionEventSchema,
    TeleopStatusSchema,
    ModeChangeSchema,
]);

export const ControlClientMessageSchema = z.union([
    AuthClientSchema,
    PingClientSchema,
    WebRtcAnswerSchema,
    WebRtcIceCandidateSchema,
    GamepadSchema,
    GamepadRegisterSchema,
    BotRegisterSchema,
    BotListRequestSchema,
    OperatorReadySchema,
    AttemptTimeoutSchema,
    ModeChangeSchema,
]);

export type ControlMessage = z.infer<typeof ControlMessageSchema>;
export type ControlClientMessage = z.infer<typeof ControlClientMessageSchema>;

export const parseControlMessage = (raw: unknown): Result<ControlMessage, ContractError> => {
    const parsed = ControlMessageSchema.safeParse(raw);
    if (!parsed.success) {
        return {
            ok: false,
            err: toContractError(parsed.error, { source: 'control-ws' }),
        };
    }
    return { ok: true, value: parsed.data };
};
